<template>
  <div>
    <!-- Navigation Bar -->
    <v-bottom-navigation
      color="info"
      horizontal
      :background-color="navigationColor"
      dark
    >
      <v-btn to="/vecprovince/commiteevecpro">
        <span>แต่งตั้งคณะกรรมการตรวจสอบ</span>
        <v-icon>mdi-account-group</v-icon>
      </v-btn>

      <v-btn to="/vecprovince/transference_personnel">
        <span>ผู้เสนอย้าย ตำแหน่งครู</span>
        <v-icon>mdi-account-switch</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <v-container fluid>
      <!-- Main Card -->
      <base-material-card
        icon="mdi-clipboard-check-outline"
        title="คณะกรรมการตรวจสอบศักยภาพของผู้ประสงค์ขอย้าย ตำแหน่งครู (การย้ายกรณีปกติ)"
        class="px-5 py-3"
      >
        <!-- Action Buttons Section -->
        <v-card class="mb-4 pa-2" elevation="2">
          <v-row align="center">
            <v-col cols="12" md="6">
              <!-- Reserved for future content -->
              
            </v-col>

            <!-- Action Buttons -->
            <v-col cols="12" md="6" class="text-right">
              <v-btn-toggle class="d-flex flex-wrap justify-end">
                <!-- Add Committee Button -->
                <v-btn
                  v-if="canAddCommittee"
                  color="primary"
                  depressed
                  @click="openAddDialog"
                  class="mb-2"
                >
                  <v-icon left>mdi-plus-circle-outline</v-icon>
                  เพิ่มรายการ
                </v-btn>

                <!-- Clear Committee Button -->
                <v-btn
                  v-if="canClearCommittee"
                  color="error"
                  depressed
                  @click="confirmClearCommittee"
                  class="mb-2 ml-2"
                >
                  <v-icon left>mdi-delete</v-icon>
                  ยกเลิกรายการ
                </v-btn>

                <!-- Confirm Committee Button -->
                <v-btn
                  v-if="canConfirmCommittee"
                  color="primary"
                  depressed
                  @click="openConfirmDialog"
                  class="mb-2 ml-2"
                >
                  <v-icon left>mdi-account-check</v-icon>
                  ยืนยันรายชื่อคณะกรรมการ
                </v-btn>

                <!-- Print Committee Button -->
                <v-btn
                  v-if="isCommitteeConfirmed"
                  color="info"
                  rounded
                  :href="
                    `#/vecprovince/print_committee_vp/${currentUser.user_province_ID}`
                  "
                  target="_blank"
                  class="mb-2 ml-2"
                >
                  <v-icon left>mdi-printer</v-icon>
                  พิมพ์รายชื่อคณะกรรมการ
                </v-btn>

                <!-- Print Attached Document Button -->
                <v-btn
                  v-if="committeeReport.commiteevecreportFile"
                  color="info"
                  :href="`/HRvecfiles/${committeeReport.commiteevecreportFile}`"
                  target="_blank"
                  class="mb-2 ml-2"
                >
                  <v-icon left>mdi-file-pdf-box</v-icon>
                  พิมพ์เอกสารที่แนบ
                </v-btn>
              </v-btn-toggle>

              <!-- File Upload Section -->
              <v-form
                v-if="
                  isCommitteeConfirmed && !committeeReport.commiteevecreportFile
                "
                ref="fileUploadForm"
                lazy-validation
                class="mt-4"
              >
                <v-alert type="info" dense text>
                  แนบไฟล์รายชื่อคณะกรรมการที่ได้รับการลงลายมือชื่อรับรอง
                </v-alert>

                <v-file-input
                  v-model="uploadFile"
                  accept=".pdf"
                  color="primary"
                  counter
                  label="ไฟล์ .pdf"
                  placeholder="เลือกแนบไฟล์ PDF"
                  prepend-icon="mdi-paperclip"
                  outlined
                  dense
                  :show-size="1000"
                  :rules="fileRules"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip color="primary" dark label small>
                      {{ text }}
                    </v-chip>
                  </template>
                </v-file-input>

                <v-btn
                  color="success"
                  @click="uploadCommitteeFile"
                  :loading="isUploading"
                  :disabled="!uploadFile"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  บันทึกไฟล์
                </v-btn>
              </v-form>
            </v-col>

            <!-- Instructions Alert -->
            <v-col cols="12">
              <v-alert
                text
                dense
                color="teal"
                icon="mdi-information-outline"
                border="left"
              >
                <div class="text-body-2">
                  <strong>ขั้นตอนการดำเนินการ:</strong>
                  <ol class="mt-2">
                    <li>
                      สำนักงานอาชีวศึกษาจังหวัดดำเนินการตั้งคณะกรรมการตรวจสอบ
                    </li>
                    <li>ตรวจสอบผู้ประสงค์ขอย้าย ตำแหน่งครู และบันทึกยืนยัน</li>
                    <li>พิมพ์รายชื่อคณะกรรมการ</li>
                  </ol>
                  <v-divider class="my-2"></v-divider>
                  <strong class="error--text">
                    <v-icon color="error" small>mdi-alert</v-icon>
                    หมายเหตุ: เมื่อกดปุ่มยืนยันรายชื่อคณะกรรมการแล้ว
                    จะไม่สามารถแก้ไขข้อมูลได้
                    กรุณาตรวจสอบให้ถูกต้องก่อนการยืนยัน
                  </strong>
                </div>
              </v-alert>

              <!-- Committee Completion Warning -->
              <v-alert
                v-if="!isCommitteeComplete"
                type="error"
                dense
                prominent
                class="mt-2"
              >
                <v-row align="center">
                  <v-col class="grow">
                    <strong>เพิ่มคณะกรรมการให้ครบถ้วน (ต้องมี 7-9 คน)</strong>
                    <div>
                      ปัจจุบันมี {{ committeeCount }} คน จาก 7-9 คนที่ต้องการ
                      <ul class="mt-2">
                        <li>ประธานกรรมการ 1 คน</li>
                        <li>กรรมการ 5-7 คน</li>
                        <li>เลขานุการ 1 คน</li>
                      </ul>
                    </div>
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>
          </v-row>
        </v-card>

        <!-- Search Field -->
        <v-text-field
          v-model="searchQuery"
          append-icon="mdi-magnify"
          label="ค้นหา ระบุคำ หรือส่วนข้อความที่เกี่ยวข้อง"
          single-line
          hide-details
          dense
          filled
          clearable
          class="mb-4"
        />

        <!-- Committee Members Table -->
        <v-data-table
          :loading="isLoading"
          :headers="tableHeaders"
          :items="committeeMembers"
          :search="searchQuery"
          class="elevation-1"
          :items-per-page="10"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100]
          }"
        >
          <!-- Province Column -->
          <template v-slot:[`item.user_lastname`]="{ item }">
            <span class="font-weight-medium">{{ item.user_lastname }}</span>
          </template>

          <!-- Year Column -->
          <template v-slot:[`item.commiteeVecProYear`]="{ item }">
            <v-chip small outlined color="info">
              {{ item.commiteeVecProYear }}
            </v-chip>
          </template>

          <!-- Time Column -->
          <template v-slot:[`item.commiteeVecProTime`]="{ item }">
            <v-chip small outlined color="success">
              ครั้งที่ {{ item.commiteeVecProTime }}
            </v-chip>
          </template>

          <!-- Name Column -->
          <template v-slot:[`item.fullName`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" color="primary" class="mr-2">
                <span class="white--text text-caption">
                  {{ getInitials(item) }}
                </span>
              </v-avatar>
              <span>{{ getFullName(item) }}</span>
            </div>
          </template>

          <!-- Position Column -->
          <template v-slot:[`item.commiteeVecProPosition`]="{ item }">
            <v-chip
              small
              :color="getPositionColor(item.commiteeVecProPosition)"
              dark
            >
              {{ getPositionLabel(item.commiteeVecProPosition) }}
            </v-chip>
          </template>

          <!-- Actions Column -->
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom v-if="!isCommitteeConfirmed">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="warning"
                  @click="openEditDialog(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>แก้ไขข้อมูล</span>
            </v-tooltip>

            <v-tooltip bottom v-if="!isCommitteeConfirmed">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="error"
                  @click="deleteMember(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>ลบรายการ</span>
            </v-tooltip>

            <v-chip v-else small color="success" dark>
              <v-icon small left>mdi-check-circle</v-icon>
              ยืนยันแล้ว
            </v-chip>
          </template>

          <!-- No Results -->
          <template v-slot:no-data>
            <v-alert type="info" text>
              ไม่พบข้อมูลคณะกรรมการ กรุณาเพิ่มข้อมูล
            </v-alert>
          </template>

          <!-- No Search Results -->
          <template v-slot:no-results>
            <v-alert type="warning" text>
              ไม่พบผลลัพธ์สำหรับ "{{ searchQuery }}"
            </v-alert>
          </template>
        </v-data-table>
      </base-material-card>

      <!-- Add Committee Dialog -->
      <v-dialog v-model="dialogs.add" persistent max-width="800px" scrollable>
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-account-multiple-plus</v-icon>
            เพิ่มกรรมการ (ทีละ 1 คน)
          </v-card-title>

          <v-card-subtitle class="mt-2">
            <v-chip small color="info" class="mr-2">
              ปี {{ currentPeriod.period_vecprovince_year }}
            </v-chip>
            <v-chip small color="success" class="mr-2">
              ครั้งที่ {{ currentPeriod.period_vecprovince_times }}
            </v-chip>
            <v-chip small color="warning">
              มีแล้ว {{ committeeCount }} คน
            </v-chip>
          </v-card-subtitle>

          <v-divider></v-divider>

          <v-card-text class="pt-4">
            <v-form ref="addForm" lazy-validation>
              <v-alert type="info" dense text class="mb-4">
                <div>
                  <strong>โครงสร้างคณะกรรมการ (7-9 คน):</strong>
                  <ul class="mt-2">
                    <li>ประธานกรรมการ 1 คน</li>
                    <li>กรรมการ 5-7 คน</li>
                    <li>เลขานุการ 1 คน</li>
                  </ul>
                </div>
              </v-alert>

              <!-- Position Selection -->
              <v-select
                v-model="newMember.position"
                :items="availablePositions"
                item-text="text"
                item-value="value"
                label="เลือกตำแหน่งกรรมการ"
                prepend-icon="mdi-badge-account"
                outlined
                dense
                :rules="[v => !!v || 'กรุณาเลือกตำแหน่ง']"
                class="mb-4"
              >
                <template v-slot:selection="{ item }">
                  <v-chip small :color="getPositionColor(item.value)" dark>
                    {{ item.text }}
                  </v-chip>
                </template>
              </v-select>

              <!-- Personnel Selection -->
              <v-autocomplete
                v-model="newMember.idCard"
                :items="availablePersonnel"
                item-text="frist_names"
                item-value="id_card"
                prepend-icon="mdi-account-search"
                label="ค้นหา: ชื่อ-นามสกุล"
                :rules="[v => !!v || 'กรุณาเลือกบุคลากร']"
                outlined
                clearable
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      {{ item.frist_names }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ item.position_name }} | {{ item.rang_name }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="text-caption">
                      {{ item.college_name }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <!-- Selected Person Info -->
              <v-alert
                v-if="selectedPersonInfo"
                dense
                text
                type="success"
                class="mt-4"
              >
                <div class="text-body-2">
                  <strong>{{ selectedPersonInfo.frist_names }}</strong>
                  <br />
                  {{ selectedPersonInfo.position_name }}
                  <br />
                  {{ selectedPersonInfo.college_name }}
                </div>
              </v-alert>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn large outlined @click="closeAddDialog">
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn
              large
              color="success"
              @click="saveCommitteeMember"
              :loading="isSaving"
              :disabled="!newMember.position || !newMember.idCard"
            >
              <v-icon left>mdi-content-save</v-icon>
              บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Committee Dialog -->
      <v-dialog v-model="dialogs.edit" persistent max-width="600px">
        <v-card>
          <v-card-title class="warning white--text">
            <v-icon left color="white">mdi-pencil</v-icon>
            แก้ไขข้อมูลคณะกรรมการ
          </v-card-title>

          <v-card-subtitle class="mt-2">
            <v-chip small color="info" class="mr-2">
              ปี {{ currentPeriod.period_vecprovince_year }}
            </v-chip>
            <v-chip small color="success">
              ครั้งที่ {{ currentPeriod.period_vecprovince_times }}
            </v-chip>
          </v-card-subtitle>

          <v-divider></v-divider>

          <v-card-text class="pt-4">
            <v-form ref="editForm" lazy-validation>
              <v-alert type="info" dense text class="mb-4">
                <strong>{{ getFullName(editingMember) }}</strong>
              </v-alert>

              <v-select
                v-model="editingMember.commiteeVecProPosition"
                :items="getEditablePositions(editingMember.commiteeVecProPosition)"
                item-text="text"
                item-value="value"
                label="เลือกตำแหน่งกรรมการ"
                prepend-icon="mdi-badge-account"
                outlined
                dense
                :rules="[v => !!v || 'กรุณาเลือกตำแหน่ง']"
              >
                <template v-slot:selection="{ item }">
                  <v-chip small :color="getPositionColor(item.value)" dark>
                    {{ item.text }}
                  </v-chip>
                </template>
              </v-select>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn large outlined @click="closeEditDialog">
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn
              large
              color="warning"
              @click="updateCommitteeMember"
              :loading="isSaving"
            >
              <v-icon left>mdi-content-save</v-icon>
              บันทึกการแก้ไข
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirm Committee Dialog -->
      <v-dialog
        v-model="dialogs.confirm"
        persistent
        max-width="900px"
        scrollable
      >
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-check-decagram</v-icon>
            ยืนยันรายชื่อคณะกรรมการ
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pt-4" style="max-height: 600px;">
            <v-alert type="warning" prominent border="left">
              <v-row align="center">
                <v-col class="grow">
                  <strong>คำเตือนสำคัญ:</strong>
                  <div class="mt-2">
                    เมื่อกดปุ่มยืนยัน คุณจะไม่สามารถแก้ไขข้อมูลใดๆ ได้
                    กรุณาตรวจสอบความถูกต้องอย่างละเอียดก่อนยืนยัน
                  </div>
                </v-col>
              </v-row>
            </v-alert>

            <v-data-table
              :headers="tableHeaders"
              :items="committeeMembers"
              class="elevation-1 mt-4"
              hide-default-footer
              disable-pagination
            >
              <template v-slot:[`item.fullName`]="{ item }">
                {{ getFullName(item) }}
              </template>

              <template v-slot:[`item.commiteeVecProPosition`]="{ item }">
                <v-chip
                  small
                  :color="getPositionColor(item.commiteeVecProPosition)"
                  dark
                >
                  {{ getPositionLabel(item.commiteeVecProPosition) }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn large outlined @click="closeConfirmDialog">
              <v-icon left>mdi-close</v-icon>
              ปิด
            </v-btn>
            <v-btn
              large
              color="primary"
              @click="confirmCommittee"
              :loading="isSaving"
            >
              <v-icon left>mdi-check-bold</v-icon>
              ยืนยันรายการ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar for Notifications -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
      >
        <v-icon left dark>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "CommitteeManagement",

  data() {
    return {
      // API Configuration
      apiKey: "HRvec2021",

      // Loading States
      isLoading: false,
      isSaving: false,
      isUploading: false,

      // Search
      searchQuery: "",

      // Dialog States
      dialogs: {
        add: false,
        edit: false,
        confirm: false
      },

      // User & Period Data
      currentUser: {},
      currentPeriod: {},

      // Committee Data
      committeeMembers: [],
      committeeReport: {},
      availablePersonnel: [],
      transferPersonnel: [],

      // Form Data
      newMember: {
        position: null,
        idCard: null
      },
      editingMember: {},
      uploadFile: null,

      // Position Options - รองรับ 7-9 คน
      allPositionOptions: [
        { text: "ประธานกรรมการ", value: "1" },
        { text: "กรรมการ ลำดับที่ 1", value: "2" },
        { text: "กรรมการ ลำดับที่ 2", value: "3" },
        { text: "กรรมการ ลำดับที่ 3", value: "4" },
        { text: "กรรมการ ลำดับที่ 4", value: "5" },
        { text: "กรรมการ ลำดับที่ 5", value: "6" },
        { text: "กรรมการ ลำดับที่ 6", value: "7" },
        { text: "กรรมการ ลำดับที่ 7", value: "8" },
        { text: "เลขานุการ", value: "9" }
      ],

      // Table Configuration
      tableHeaders: [
        { text: "สอจ.", value: "user_lastname", align: "center", width: "10%" },
        {
          text: "ปี",
          value: "commiteeVecProYear",
          align: "center",
          width: "10%"
        },
        {
          text: "ครั้งที่",
          value: "commiteeVecProTime",
          align: "center",
          width: "10%"
        },
        {
          text: "ชื่อ-นามสกุล",
          value: "fullName",
          align: "left",
          width: "30%"
        },
        {
          text: "ตำแหน่ง",
          value: "commiteeVecProPosition",
          align: "center",
          width: "20%"
        },
        {
          text: "จัดการ",
          value: "actions",
          align: "center",
          sortable: false,
          width: "15%"
        }
      ],

      // File Upload Rules
      fileRules: [v => !v || v.size < 1048576 || "ไฟล์ต้องมีขนาดไม่เกิน 1 MB"],

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "success",
        icon: "mdi-check-circle",
        timeout: 3000
      }
    };
  },

  computed: {
    // Navigation color
    navigationColor() {
      return "#1D386A";
    },

    // Current date
    currentDate() {
      return new Date().toISOString();
    },

    // Committee status checks
    isCommitteeConfirmed() {
      return this.committeeReport.commiteevecreportStatus === "1";
    },

    committeeCount() {
      return this.committeeMembers.length;
    },

    // ต้องมี 7-9 คน
    isCommitteeComplete() {
      return this.committeeCount >= 7 && this.committeeCount <= 9;
    },

    // ตรวจสอบจำนวนกรรมการแต่ละประเภท
    hasChairman() {
      return this.committeeMembers.some(m => m.commiteeVecProPosition === "1");
    },

    hasSecretary() {
      return this.committeeMembers.some(m => m.commiteeVecProPosition === "9");
    },

    regularMembersCount() {
      return this.committeeMembers.filter(m => {
        const pos = m.commiteeVecProPosition;
        return pos >= "2" && pos <= "8";
      }).length;
    },

    // ตำแหน่งที่ยังว่างอยู่
    availablePositions() {
      const usedPositions = this.committeeMembers.map(
        m => m.commiteeVecProPosition
      );
      
      return this.allPositionOptions.filter(pos => {
        // ประธานและเลขา: ถ้ามีแล้วไม่ให้เลือกซ้ำ
        if (pos.value === "1" || pos.value === "9") {
          return !usedPositions.includes(pos.value);
        }
        // กรรมการ: ให้เลือกได้ถ้ายังไม่ถึง 7 คน
        return this.regularMembersCount < 7;
      });
    },

    // Button visibility
    canAddCommittee() {
      return !this.isCommitteeConfirmed && this.committeeCount < 9;
    },

    canClearCommittee() {
      return !this.isCommitteeConfirmed && this.committeeCount > 0;
    },

    canConfirmCommittee() {
      return (
        !this.isCommitteeConfirmed &&
        this.isCommitteeComplete &&
        this.hasChairman &&
        this.hasSecretary &&
        this.regularMembersCount >= 5
      );
    },

    // Check if there are transfer personnel
    hasTransferPersonnel() {
      return this.transferPersonnel.length > 0;
    },

    // Selected person info
    selectedPersonInfo() {
      if (!this.newMember.idCard) return null;
      return this.availablePersonnel.find(
        p => p.id_card === this.newMember.idCard
      );
    }
  },

  async mounted() {
    await this.initialize();
  },

  methods: {
    // ============================================
    // Initialization Methods
    // ============================================

    async initialize() {
      try {
        await this.loadCurrentUser();
        await this.loadPeriod();
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
        console.error("Initialization error:", error);
      }
    },

    async loadCurrentUser() {
      const userSession = JSON.parse(sessionStorage.getItem("user")) || {};
      const response = await this.$http.post("admin.php", {
        user_name: userSession.user_name,
        provice: "check",
        ApiKey: this.apiKey
      });
      this.currentUser = response.data;
    },

    async loadPeriod() {
      const response = await this.$http.post("period_vecprovince.php", {
        ApiKey: this.apiKey,
        period_vecprovince_enable: "1",
        period_vecprovinc_id: "301"
      });

      this.currentPeriod = response.data;

      if (this.currentPeriod.period_vecprovince_enable !== "1") {
        await swal.fire({
          icon: "warning",
          title: "ไม่ถึงกำหนดการเปิดระบบ",
          showConfirmButton: false,
          timer: 3000
        });
        this.$router.push("/vecprovince");
        return;
      }

      await Promise.all([
        this.loadCommitteeMembers(),
        this.loadCommitteeReport(),
        this.loadTransferPersonnel()
      ]);
    },

    // ============================================
    // Data Loading Methods
    // ============================================

    async loadCommitteeMembers() {
      this.isLoading = true;
      try {
        const response = await this.$http.post("commiteevecpro.php", {
          ApiKey: this.apiKey,
          commiteevecproviceID: this.currentUser.user_province_ID,
          commiteeVecProYear: this.currentPeriod.period_vecprovince_year,
          commiteeVecProTime: this.currentPeriod.period_vecprovince_times
        });
        this.committeeMembers = response.data;
      } catch (error) {
        this.showError("ไม่สามารถโหลดข้อมูลคณะกรรมการได้");
      } finally {
        this.isLoading = false;
      }
    },

    async loadCommitteeReport() {
      try {
        const response = await this.$http.post("commiteevecreport.php", {
          ApiKey: this.apiKey,
          commiteevecreportproviceID: this.currentUser.user_province_ID,
          commiteevecreportYear: this.currentPeriod.period_vecprovince_year,
          commiteevecreportTime: this.currentPeriod.period_vecprovince_times
        });
        this.committeeReport = response.data;
      } catch (error) {
        console.error("Error loading committee report:", error);
      }
    },

    async loadTransferPersonnel() {
      try {
        const response = await this.$http.post("transference_personnel.php", {
          ApiKey: this.apiKey,
          province_ID: this.currentUser.user_province_ID,
          time_s: this.currentPeriod.period_vecprovince_times,
          year_s: this.currentPeriod.period_vecprovince_year,
          type_move: "nm"
        });
        this.transferPersonnel = response.data;

        if (this.transferPersonnel.length === 0) {
          await swal.fire({
            icon: "warning",
            title: "ไม่พบผู้ยื่นขอย้ายในสำนักงานอาชีวศึกษาจังหวัด",
            showConfirmButton: false,
            timer: 3000
          });
        }
      } catch (error) {
        console.error("Error loading transfer personnel:", error);
      }
    },

    async loadAvailablePersonnel() {
      try {
        const response = await this.$http.post("personnel_temporary.php", {
          ApiKey: this.apiKey,
          province_ID: this.currentUser.user_province_ID,
          committees: "ok"
        });
        this.availablePersonnel = response.data;
      } catch (error) {
        this.showError("ไม่สามารถโหลดข้อมูลบุคลากรได้");
      }
    },

    // ============================================
    // Dialog Management Methods
    // ============================================

    async openAddDialog() {
      await this.loadAvailablePersonnel();
      this.newMember = {
        position: null,
        idCard: null
      };
      this.dialogs.add = true;
    },

    closeAddDialog() {
      this.dialogs.add = false;
      this.newMember = {
        position: null,
        idCard: null
      };
      if (this.$refs.addForm) {
        this.$refs.addForm.reset();
      }
    },

    async openEditDialog(member) {
      this.editingMember = { ...member };
      this.dialogs.edit = true;
    },

    closeEditDialog() {
      this.dialogs.edit = false;
      this.editingMember = {};
      if (this.$refs.editForm) {
        this.$refs.editForm.reset();
      }
    },

    openConfirmDialog() {
      this.dialogs.confirm = true;
    },

    closeConfirmDialog() {
      this.dialogs.confirm = false;
    },

    // ============================================
    // Committee Management Methods
    // ============================================

    async saveCommitteeMember() {
      if (!this.$refs.addForm.validate()) {
        this.showWarning("กรุณากรอกข้อมูลให้ครบถ้วน");
        return;
      }

      // ตรวจสอบว่าบุคคลนี้ถูกเลือกไปแล้วหรือไม่
      const isDuplicate = this.committeeMembers.some(
        m => m.commiteeVecProIdCard === this.newMember.idCard
      );

      if (isDuplicate) {
        this.showWarning("บุคลากรท่านนี้ถูกเลือกเป็นกรรมการไปแล้ว");
        return;
      }

      this.isSaving = true;
      try {
        let data = {
          ApiKey: this.apiKey,
          commiteevecproviceID: this.currentUser.user_province_ID,
          commiteeVecProYear: this.currentPeriod.period_vecprovince_year,
          commiteeVecProTime: this.currentPeriod.period_vecprovince_times,
          commiteeVecProIdCard: this.newMember.idCard,
          commiteeVecProPosition: this.newMember.position
        };
        console.log(data)
       let result =  await this.$http.post("commiteevecpro.insert.php", data);
        console.log(result.data)
        this.showSuccess("เพิ่มกรรมการเรียบร้อย");
        await this.loadCommitteeMembers();
        this.closeAddDialog();
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
      } finally {
        this.isSaving = false;
      }
    },

    async updateCommitteeMember() {
      if (!this.$refs.editForm.validate()) {
        this.showWarning("กรุณากรอกข้อมูลให้ครบถ้วน");
        return;
      }

      this.isSaving = true;
      try {
        const data = {
          ApiKey: this.apiKey,
          commiteevecproviceID: this.currentUser.user_province_ID,
          commiteeVecProYear: this.currentPeriod.period_vecprovince_year,
          commiteeVecProTime: this.currentPeriod.period_vecprovince_times,
          commiteeVecProIdCard: this.editingMember.commiteeVecProIdCard,
          commiteeVecProPosition: this.editingMember.commiteeVecProPosition
        };

        const response = await this.$http.post(
          "commiteevecpro.update.php",
          data
        );

        if (response.data.status) {
          this.showSuccess("แก้ไขข้อมูลเรียบร้อย");
          await this.loadCommitteeMembers();
          this.closeEditDialog();
        } else {
          this.showError("เกิดข้อผิดพลาดในการแก้ไขข้อมูล");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการแก้ไขข้อมูล");
      } finally {
        this.isSaving = false;
      }
    },

    async deleteMember(member) {
      const result = await swal.fire({
        title: "ยืนยันการลบ",
        text: `คุณต้องการลบ ${this.getFullName(member)} ออกจากคณะกรรมการหรือไม่?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#F44336",
        cancelButtonColor: "#9E9E9E"
      });

      if (!result.isConfirmed) return;

      try {
        const data = {
          ApiKey: this.apiKey,
          commiteevecproviceID: this.currentUser.user_province_ID,
          commiteeVecProYear: this.currentPeriod.period_vecprovince_year,
          commiteeVecProTime: this.currentPeriod.period_vecprovince_times,
          commiteeVecProIdCard: member.commiteeVecProIdCard
        };

        const response = await this.$http.post(
          "commiteevecpro.delete.single.php",
          data
        );

        if (response.data.status) {
          this.showSuccess("ลบรายการเรียบร้อย");
          await this.loadCommitteeMembers();
        } else {
          this.showError("เกิดข้อผิดพลาดในการลบรายการ");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการลบรายการ");
      }
    },

    async confirmCommittee() {
      const result = await swal.fire({
        title: "ยืนยันรายชื่อคณะกรรมการ",
        text: "เมื่อยืนยันแล้วจะไม่สามารถแก้ไขได้ ต้องการดำเนินการต่อหรือไม่?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#4CAF50",
        cancelButtonColor: "#F44336"
      });

      if (!result.isConfirmed) return;

      this.isSaving = true;
      try {
        // Update committee status
        const committeeStatusData = {
          ApiKey: this.apiKey,
          commiteevecproviceID: this.currentUser.user_province_ID,
          commiteeVecProYear: this.currentPeriod.period_vecprovince_year,
          commiteeVecProTime: this.currentPeriod.period_vecprovince_times,
          commiteevecproStatus: "1"
        };

        // Insert committee report
        const reportData = {
          ApiKey: this.apiKey,
          commiteevecreportproviceID: this.currentUser.user_province_ID,
          commiteevecreportYear: this.currentPeriod.period_vecprovince_year,
          commiteevecreportTime: this.currentPeriod.period_vecprovince_times,
          commiteevecreportStatus: "1",
          commiteevecreportDateTime: this.currentDate
        };
        console.log(reportData)
        await Promise.all([
          this.$http.post(
            "commiteevecpro.update.status.php",
            committeeStatusData
          ),
          this.$http.post("commiteevecreport.insert.php", reportData)
        ]);

        this.showSuccess("ยืนยันรายชื่อคณะกรรมการเรียบร้อย");
        await Promise.all([
          this.loadCommitteeMembers(),
          this.loadCommitteeReport()
        ]);
        this.closeConfirmDialog();
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการยืนยันรายชื่อ");
      } finally {
        this.isSaving = false;
      }
    },

    async confirmClearCommittee() {
      const result = await swal.fire({
        title: "ยกเลิกรายการคณะกรรมการ",
        text: "คุณต้องการยกเลิกรายการคณะกรรมการทั้งหมดหรือไม่?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#F44336",
        cancelButtonColor: "#9E9E9E"
      });

      if (!result.isConfirmed) return;

      try {
        const data = {
          ApiKey: this.apiKey,
          commiteevecproviceID: this.currentUser.user_province_ID,
          commiteeVecProYear: this.currentPeriod.period_vecprovince_year,
          commiteeVecProTime: this.currentPeriod.period_vecprovince_times
        };

        const response = await this.$http.post(
          "commiteevecpro.delete.php",
          data
        );

        if (response.data.status) {
          this.showSuccess("ยกเลิกรายการเรียบร้อย");
          await this.loadCommitteeMembers();
        } else {
          this.showError("เกิดข้อผิดพลาดในการยกเลิกรายการ");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการยกเลิกรายการ");
      }
    },

    // ============================================
    // File Upload Methods
    // ============================================

    async uploadCommitteeFile() {
      if (!this.$refs.fileUploadForm.validate() || !this.uploadFile) {
        this.showWarning("กรุณาเลือกไฟล์ที่ต้องการอัพโหลด");
        return;
      }

      this.isUploading = true;
      try {
        const formData = new FormData();
        const filename = `${this.currentUser.user_province_ID}${this.currentPeriod.period_vecprovince_year}${this.currentPeriod.period_vecprovince_times}committeevp.pdf`;

        formData.append("ApiKey", this.apiKey);
        formData.append("file", this.uploadFile);
        formData.append("filename", `../HRvecfiles/${filename}`);

        const uploadResponse = await this.$http.post(
          "uploadfile.php",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" }
          }
        );

        if (uploadResponse.data.status) {
          const updateData = {
            ApiKey: this.apiKey,
            commiteevecreportproviceID: this.currentUser.user_province_ID,
            commiteevecreportYear: this.currentPeriod.period_vecprovince_year,
            commiteevecreportTime: this.currentPeriod.period_vecprovince_times,
            commiteevecreportFile: filename,
            commiteevecreportFileDate: this.currentDate
          };

          const updateResponse = await this.$http.post(
            "commiteevecreport.update.php",
            updateData
          );

          if (updateResponse.data.status) {
            this.showSuccess("อัพโหลดไฟล์เรียบร้อย");
            await this.loadCommitteeReport();
            this.uploadFile = null;
          } else {
            this.showError("เกิดข้อผิดพลาดในการบันทึกข้อมูลไฟล์");
          }
        } else {
          this.showError("เกิดข้อผิดพลาดในการอัพโหลดไฟล์");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการอัพโหลดไฟล์");
      } finally {
        this.isUploading = false;
      }
    },

    // ============================================
    // Helper Methods
    // ============================================

    getFullName(item) {
      if (!item) return "";
      return `${item.title_s || ""}${item.frist_name || ""} ${item.last_name ||
        ""}`.trim();
    },

    getInitials(item) {
      if (!item || !item.frist_name) return "";
      return item.frist_name.charAt(0).toUpperCase();
    },

    getPositionLabel(position) {
      const pos = this.allPositionOptions.find(p => p.value === position);
      return pos ? pos.text : "ไม่ระบุ";
    },

    getPositionColor(position) {
      if (position === "1") return "red darken-2"; // ประธาน
      if (position === "9") return "green"; // เลขานุการ
      return "blue"; // กรรมการ
    },

    // ตำแหน่งที่สามารถแก้ไขได้ (ไม่รวมตำแหน่งที่มีคนอยู่แล้ว)
    getEditablePositions(currentPosition) {
      const usedPositions = this.committeeMembers
        .filter(m => m.commiteeVecProPosition !== currentPosition)
        .map(m => m.commiteeVecProPosition);

      return this.allPositionOptions.filter(pos => {
        // ถ้าเป็นตำแหน่งเดิม ให้เลือกได้
        if (pos.value === currentPosition) return true;

        // ถ้าเป็นประธานหรือเลขา ต้องไม่มีคนอยู่แล้ว
        if (pos.value === "1" || pos.value === "9") {
          return !usedPositions.includes(pos.value);
        }

        // กรรมการ: ให้เลือกได้ถ้ายังไม่ถึง 7 คน
        const regularCount = this.committeeMembers.filter(m => {
          const p = m.commiteeVecProPosition;
          return p >= "2" && p <= "8" && p !== currentPosition;
        }).length;
        
        return regularCount < 7;
      });
    },

    // ============================================
    // Notification Methods
    // ============================================

    showSuccess(message) {
      this.snackbar = {
        show: true,
        text: message,
        color: "success",
        icon: "mdi-check-circle",
        timeout: 3000
      };
    },

    showError(message) {
      this.snackbar = {
        show: true,
        text: message,
        color: "error",
        icon: "mdi-alert-circle",
        timeout: 5000
      };
    },

    showWarning(message) {
      this.snackbar = {
        show: true,
        text: message,
        color: "warning",
        icon: "mdi-alert",
        timeout: 4000
      };
    },

    showInfo(message) {
      this.snackbar = {
        show: true,
        text: message,
        color: "info",
        icon: "mdi-information",
        timeout: 3000
      };
    }
  }
};
</script>

<style scoped>
/* Custom Styles */
.v-data-table >>> thead th {
  font-size: 14px !important;
  font-weight: 600 !important;
  background-color: #f5f5f5;
}

.v-data-table >>> tbody td {
  font-size: 13px !important;
}

.v-chip {
  font-weight: 500;
}

/* Responsive table */
@media screen and (max-width: 960px) {
  .v-data-table >>> thead th {
    font-size: 12px !important;
  }

  .v-data-table >>> tbody td {
    font-size: 11px !important;
  }
}

/* Dialog transitions */
.v-dialog {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

/* Button hover effects */
.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

/* Card elevation */
.v-card {
  transition: box-shadow 0.3s ease;
}

/* Alert styling */
.v-alert {
  border-radius: 8px;
}

/* Table row hover */
.v-data-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>